// DJ Polls / Survey Service
// Allows DJs to create polls and surveys for attendees

import { collection, doc, addDoc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, Timestamp, type DocumentData } from 'firebase/firestore';
import { db } from './firebase';

export interface PollQuestion {
  id: string;
  type: 'multiple-choice' | 'free-text' | 'rating' | 'yes-no';
  text: string;
  required: boolean;
  options?: string[]; // for multiple-choice
  ratingMax?: number; // for rating, default 5
}

export interface PollSettings {
  mandatory: boolean;
  showToNewUsers: boolean;
  editable: boolean;
  showResultsToAttendees: boolean;
}

export const DEFAULT_POLL_SETTINGS: PollSettings = {
  mandatory: false,
  showToNewUsers: false,
  editable: false,
  showResultsToAttendees: false,
};

export type PollArrival = 'draft' | 'live' | 'scheduled';

// Same list as AUTO_SEND_OFFSETS_MINUTES in functions/src/utils/pollAutoSend.ts and the polls rules
export const AUTO_SEND_OFFSETS_MINUTES = [-60, -30, -15, 0, 15, 30, 60] as const;

export type AutoSendResult = 'sent' | 'eventEnded' | 'noStartTime';

export function isAllowedAutoSendOffset(value: unknown): value is number {
  return typeof value === 'number' && (AUTO_SEND_OFFSETS_MINUTES as readonly number[]).includes(value);
}

export interface Poll {
  id: string;
  eventId: string;
  djId: string;
  title: string;
  questions: PollQuestion[];
  status: 'draft' | 'active' | 'closed';
  mandatory: boolean;
  showResultsToAttendees: boolean;
  showToNewUsers: boolean;
  editable: boolean;
  lastResendAt?: string;
  createdAt: string;
  closedAt?: string;
  responseCount: number;
  autoSendOffsetMinutes?: number | null;
  autoSendDueAt?: string | null;
  autoSendResult?: AutoSendResult | null;
  autoSentAt?: string | null;
  fromTemplateId?: string | null;
}

function timestampToIso(value: unknown): string | null {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  return typeof value === 'string' ? value : null;
}

export function docToPoll(id: string, data: DocumentData): Poll {
  return {
    id,
    ...data,
    autoSendDueAt: timestampToIso(data.autoSendDueAt),
    autoSentAt: timestampToIso(data.autoSentAt),
  } as Poll;
}

// A draft is never shown to attendees, even with "Show to new users" on
export function isPollVisibleToAttendees(poll: Pick<Poll, 'status' | 'showToNewUsers'>): boolean {
  return poll.status === 'active' || (poll.status === 'closed' && poll.showToNewUsers);
}

export function buildSendNowUpdate(): Pick<Poll, 'status' | 'autoSendOffsetMinutes'> {
  return { status: 'active', autoSendOffsetMinutes: null };
}

export function buildCancelAutoSendUpdate(): Pick<Poll, 'autoSendOffsetMinutes'> {
  return { autoSendOffsetMinutes: null };
}

export interface PollAnswer {
  questionId: string;
  value: string | number;
}

export interface PollResponse {
  id: string;
  pollId: string;
  eventId: string;
  respondentId: string;
  answers: PollAnswer[];
  submittedAt: string;
}

export interface PollResults {
  pollId: string;
  totalResponses: number;
  questionResults: QuestionResult[];
}

export interface QuestionResult {
  questionId: string;
  questionText: string;
  questionType: PollQuestion['type'];
  // Multiple choice: option -> count
  optionCounts?: Map<string, number>;
  // Rating: average, distribution
  ratingAverage?: number;
  ratingDistribution?: Map<number, number>;
  // Free text: list of responses
  freeTextResponses?: string[];
  // Yes/No: yes count, no count
  yesCount?: number;
  noCount?: number;
}

/**
 * Create a new poll.
 */
export async function createPoll(
  eventId: string,
  djId: string,
  title: string,
  questions: PollQuestion[],
  status: 'draft' | 'active' = 'draft',
  settings: PollSettings = DEFAULT_POLL_SETTINGS
): Promise<string> {
  const docRef = await addDoc(collection(db, 'polls'), {
    eventId,
    djId,
    title,
    questions,
    status,
    mandatory: settings.mandatory,
    showResultsToAttendees: settings.showResultsToAttendees,
    showToNewUsers: settings.showToNewUsers,
    editable: settings.editable,
    autoSendOffsetMinutes: null,
    createdAt: new Date().toISOString(),
    responseCount: 0,
  });
  return docRef.id;
}

/**
 * Update a poll (title, questions, status, etc.).
 */
export async function updatePoll(pollId: string, updates: Partial<Omit<Poll, 'id'>>): Promise<void> {
  const ref = doc(db, 'polls', pollId);
  await updateDoc(ref, updates);
}

/**
 * Get active polls for an event (attendee view).
 */
export async function getActivePollsForEvent(eventId: string): Promise<Poll[]> {
  const q = query(
    collection(db, 'polls'),
    where('eventId', '==', eventId),
    where('status', '==', 'active')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => docToPoll(d.id, d.data()));
}

/**
 * Get polls marked "show to new users" for an event (active or closed; drafts are filtered out).
 */
export async function getShowToNewUsersPollsForEvent(eventId: string): Promise<Poll[]> {
  const q = query(
    collection(db, 'polls'),
    where('eventId', '==', eventId),
    where('showToNewUsers', '==', true)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => docToPoll(d.id, d.data())).filter(isPollVisibleToAttendees);
}

/**
 * Get all polls for an event (DJ view).
 */
export async function getAllPollsForEvent(eventId: string): Promise<Poll[]> {
  const q = query(
    collection(db, 'polls'),
    where('eventId', '==', eventId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => docToPoll(d.id, d.data()))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * Submit a poll response.
 */
export async function submitPollResponse(
  pollId: string,
  eventId: string,
  attendeeId: string,
  answers: PollAnswer[],
  isUpdate: boolean = false
): Promise<void> {
  const responseDocId = `${pollId}_${attendeeId}`;
  const responseRef = doc(db, 'pollResponses', responseDocId);

  if (isUpdate) {
    // Overwrite answers and timestamp, do NOT increment responseCount
    await setDoc(responseRef, {
      pollId,
      eventId,
      respondentId: attendeeId,
      answers,
      submittedAt: new Date().toISOString(),
    });
    return;
  }

  // First-time submission: check for duplicate
  const already = await hasRespondedToPoll(pollId, attendeeId);
  if (already) {
    throw new Error('You have already responded to this poll');
  }

  await setDoc(responseRef, {
    pollId,
    eventId,
    respondentId: attendeeId,
    answers,
    submittedAt: new Date().toISOString(),
  });

  // Increment response count on the poll
  const pollRef = doc(db, 'polls', pollId);
  const pollSnap = await getDoc(pollRef);
  if (pollSnap.exists()) {
    const current = pollSnap.data().responseCount || 0;
    await updateDoc(pollRef, { responseCount: current + 1 });
  }
}

/**
 * Check if an attendee has already responded to a poll.
 */
export async function hasRespondedToPoll(pollId: string, attendeeId: string): Promise<boolean> {
  const q = query(
    collection(db, 'pollResponses'),
    where('pollId', '==', pollId),
    where('respondentId', '==', attendeeId)
  );
  const snap = await getDocs(q);
  return snap.size > 0;
}

/**
 * Get an attendee's previous response to a poll (for editable polls).
 */
export async function getPollResponse(
  pollId: string,
  attendeeId: string
): Promise<PollResponse | null> {
  const docId = `${pollId}_${attendeeId}`;
  const docSnap = await getDoc(doc(db, 'pollResponses', docId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as PollResponse;
}

/**
 * Get aggregated results for a poll.
 */
export async function getPollResults(pollId: string): Promise<PollResults> {
  // Get the poll to know question structure
  const pollRef = doc(db, 'polls', pollId);
  const pollSnap = await getDoc(pollRef);
  if (!pollSnap.exists()) throw new Error('Poll not found');
  const poll = docToPoll(pollSnap.id, pollSnap.data());

  // Get all responses
  const q = query(
    collection(db, 'pollResponses'),
    where('pollId', '==', pollId)
  );
  const snap = await getDocs(q);
  const responses = snap.docs.map(d => ({ id: d.id, ...d.data() } as PollResponse));

  // Build results per question
  const questionResults: QuestionResult[] = poll.questions.map(question => {
    const result: QuestionResult = {
      questionId: question.id,
      questionText: question.text,
      questionType: question.type,
    };

    const answersForQ = responses
      .map(r => r.answers.find(a => a.questionId === question.id))
      .filter((a): a is PollAnswer => a !== undefined);

    switch (question.type) {
      case 'multiple-choice': {
        const counts = new Map<string, number>();
        (question.options || []).forEach(opt => counts.set(opt, 0));
        answersForQ.forEach(a => {
          const v = String(a.value);
          counts.set(v, (counts.get(v) || 0) + 1);
        });
        result.optionCounts = counts;
        break;
      }
      case 'rating': {
        const ratings = answersForQ.map(a => Number(a.value)).filter(n => !isNaN(n));
        result.ratingAverage = ratings.length > 0 ? ratings.reduce((s, n) => s + n, 0) / ratings.length : 0;
        const dist = new Map<number, number>();
        ratings.forEach(r => dist.set(r, (dist.get(r) || 0) + 1));
        result.ratingDistribution = dist;
        break;
      }
      case 'free-text': {
        result.freeTextResponses = answersForQ.map(a => String(a.value)).filter(s => s.trim().length > 0);
        break;
      }
      case 'yes-no': {
        result.yesCount = answersForQ.filter(a => String(a.value).toLowerCase() === 'yes').length;
        result.noCount = answersForQ.filter(a => String(a.value).toLowerCase() === 'no').length;
        break;
      }
    }

    return result;
  });

  return {
    pollId,
    totalResponses: responses.length,
    questionResults,
  };
}

/**
 * Close a poll (no more responses accepted).
 */
export async function closePoll(pollId: string): Promise<void> {
  await updatePoll(pollId, {
    status: 'closed',
    closedAt: new Date().toISOString(),
  });
}

/**
 * Toggle result visibility for attendees.
 */
export async function toggleResultVisibility(pollId: string, visible: boolean): Promise<void> {
  await updatePoll(pollId, { showResultsToAttendees: visible });
}

/**
 * Resend a poll — stamps lastResendAt to trigger overlay re-display + FCM push.
 */
export async function resendPoll(pollId: string): Promise<void> {
  await updatePoll(pollId, {
    lastResendAt: new Date().toISOString(),
  });
}

/**
 * Delete a poll and its responses.
 */
export async function deletePoll(pollId: string): Promise<void> {
  // Delete all responses
  const q = query(
    collection(db, 'pollResponses'),
    where('pollId', '==', pollId)
  );
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    await deleteDoc(doc(db, 'pollResponses', d.id));
  }
  // Delete the poll
  await deleteDoc(doc(db, 'polls', pollId));
}

// ---- Poll Templates (the DJ's My Polls library, ITER-146) ----

export interface PollTemplate {
  id: string;
  djId: string;
  title: string;
  questions: PollQuestion[];
  settings: PollSettings;
  isDefault: boolean;
  eventTemplateIds: string[];
  arrival: PollArrival;
  arrivalOffsetMinutes: number;
  createdAt: string;
  updatedAt: string | null;
}

export type PollTemplateInput = Omit<PollTemplate, 'id' | 'djId' | 'createdAt' | 'updatedAt'>;

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

// Templates saved before ITER-146 hold only title and questions; the rest falls back to defaults
export function normalizePollTemplate(id: string, data: DocumentData): PollTemplate {
  const settings = (data.settings ?? {}) as Partial<PollSettings>;
  const arrival: PollArrival = data.arrival === 'live' || data.arrival === 'scheduled' ? data.arrival : 'draft';
  return {
    id,
    djId: data.djId,
    title: typeof data.title === 'string' ? data.title : '',
    questions: Array.isArray(data.questions) ? data.questions : [],
    settings: {
      mandatory: booleanOr(settings.mandatory, DEFAULT_POLL_SETTINGS.mandatory),
      showToNewUsers: booleanOr(settings.showToNewUsers, DEFAULT_POLL_SETTINGS.showToNewUsers),
      editable: booleanOr(settings.editable, DEFAULT_POLL_SETTINGS.editable),
      showResultsToAttendees: booleanOr(settings.showResultsToAttendees, DEFAULT_POLL_SETTINGS.showResultsToAttendees),
    },
    isDefault: data.isDefault === true,
    eventTemplateIds: Array.isArray(data.eventTemplateIds)
      ? data.eventTemplateIds.filter((templateId: unknown): templateId is string => typeof templateId === 'string')
      : [],
    arrival,
    arrivalOffsetMinutes: isAllowedAutoSendOffset(data.arrivalOffsetMinutes) ? data.arrivalOffsetMinutes : 0,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : new Date(0).toISOString(),
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
  };
}

/**
 * Save a poll to the library.
 */
export async function savePollTemplate(djId: string, input: PollTemplateInput): Promise<string> {
  const docRef = await addDoc(collection(db, 'pollTemplates'), {
    djId,
    ...input,
    createdAt: new Date().toISOString(),
    updatedAt: null,
  });
  return docRef.id;
}

/**
 * Replace a library poll's content and settings.
 */
export async function updatePollTemplate(templateId: string, input: PollTemplateInput): Promise<void> {
  await updateDoc(doc(db, 'pollTemplates', templateId), {
    ...input,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Get all poll templates for a DJ, newest first.
 */
export async function getPollTemplates(djId: string): Promise<PollTemplate[]> {
  const q = query(
    collection(db, 'pollTemplates'),
    where('djId', '==', djId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => normalizePollTemplate(d.id, d.data()))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * Delete a poll template.
 */
export async function deletePollTemplate(templateId: string): Promise<void> {
  await deleteDoc(doc(db, 'pollTemplates', templateId));
}
